import {defineMessage} from 'react-intl';
import * as React from 'react';
import {buildInstructions} from '../../snippets/dotnet';
import comment from '../../snippets/comment';
import {locales} from '../sampleLocations';
import {
  BuildInstructions,
  CreateShellCommand,
} from '../../../components/left-col';

const createIntent = (country, reader) => {
  if (reader === 'wpe') {
    const currency = locales[country].currency;
    const pmTypes =
      country === 'CA'
        ? `{
        "card_present",
        "interac_present",
      }`
        : `{ "card_present" }`;
    return `
{+create-intent}
  [Route("create_payment_intent")]
  [ApiController]
  public class PaymentIntentApiController : Controller
  {
    [HttpPost]
    public ActionResult Post(PaymentIntentCreateRequest request)
    {
      var service = new PaymentIntentService();

      // For Terminal payments, the 'payment_method_types' parameter must include
      // 'card_present' and the 'capture_method' must be set to 'manual'
      var options = new PaymentIntentCreateOptions
      {
          Amount = long.Parse(request.Amount),
          Currency = "${currency}",
          PaymentMethodTypes = new List<string> ${pmTypes},
          CaptureMethod = "manual",
      };
      var {span response create-intent}intent{/span} = service.Create(options);

      return Json(new { client_secret = intent.ClientSecret });
      {/create}
    }

    public class PaymentIntentCreateRequest
    {
      [JsonProperty("amount")]
      public string Amount { get; set; }
    }
  }
{/create-intent}
`;
  }
};

const createLocation = (country) => {
  const location = locales[country].location;
  const city = location.address.city
    ? `
      City = "${location.address.city}",`
    : '';
  const state = location.address.state
    ? `
      State = "${location.address.state}",`
    : '';
  return `
private static Location createLocation(){
  {+create-location}
  var options = new LocationCreateOptions
  {
    DisplayName = "${location.displayName}",
    Address = new AddressOptions
    {
      Line1 = "${location.address.line1}",${city}${state}
      Country = "${location.address.country}",
      PostalCode = "${location.address.postalCode}",
    },
  };

  var service = new LocationService();
  var location = service.Create(options);
  {/create-location}

  return location;
}
`;
};

export const steps = {
  setup: {
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.server.dotnet.setup.heading',
        defaultMessage: 'Install the Stripe.net library',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.server.dotnet.setup.body',
        defaultMessage:
          'Install the package with dotnet or NuGet. Alternatively, if you’re starting from scratch, download the files which contains a configured .csproj file.',
      }),
    },
    Component() {
      return <BuildInstructions instructions={buildInstructions} />;
    },
  },
  'create-location': {},
  'connection-token-endpoint': {},
  'create-intent': {},
  'capture-intent-endpoint': {},
  'run-server': {
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.server.dotnet.run-server.body',
        defaultMessage:
          'Run your ASP.NET MVC server and go to <a>localhost:4242</a>.',
      }),
      values: {
        a: (str) => (
          <a href="http://localhost:4242" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: CreateShellCommand('dotnet run'),
  },
};

export default ({keys, merchant, reader, country}) => `using System;
using System.Collections.Generic;
using Microsoft.AspNetCore;
using Microsoft.AspNetCore.Builder;
using Microsoft.AspNetCore.Hosting;
using Microsoft.AspNetCore.Mvc;
using Microsoft.Extensions.DependencyInjection;
using Microsoft.Extensions.Hosting;
using Newtonsoft.Json;
using Stripe;
using Stripe.Terminal;

namespace StripeExample
{
  public class Program
  {
    public static void Main(string[] args)
    {
      WebHost.CreateDefaultBuilder(args)
        .UseUrls("http://0.0.0.0:4242")
        .UseWebRoot("public")
        .UseStartup<Startup>()
        .Build()
        .Run();
    }
    ${createLocation(country)}
  }

  public class Startup
  {
    public void ConfigureServices(IServiceCollection services)
    {
      services.AddMvc().AddNewtonsoftJson();
    }

    public void Configure(IApplicationBuilder app, IWebHostEnvironment env)
    {
      {+setup}
      ${comment && comment(merchant)}
      StripeConfiguration.ApiKey = "${keys.secret}";
      {/setup}

      if (env.IsDevelopment()) app.UseDeveloperExceptionPage();
      app.UseRouting();
      app.UseDefaultFiles();
      app.UseStaticFiles();
      app.UseEndpoints(endpoints => endpoints.MapControllers());
    }
  }

  // The ConnectionToken's secret lets you connect to any Stripe Terminal reader
  // and take payments with your Stripe account.
  // Be sure to authenticate the endpoint for creating connection tokens.
  {+connection-token-endpoint}
  [Route("connection_token")]
  [ApiController]
  public class ConnectionTokenApiController : Controller
  {
    [HttpPost]
    public ActionResult Post()
    {
      var options = new ConnectionTokenCreateOptions{};
      var service = new ConnectionTokenService();
      var {span response connection-token-endpoint}connectionToken{/span} = service.Create(options);

      return Json(new {secret = connectionToken.Secret});
    }
  }
  {/connection-token-endpoint}
  ${createIntent(country, reader)}
  {+capture-intent-endpoint}
  [Route("capture_payment_intent")]
  [ApiController]
  public class CapturePaymentIntentApiController : Controller
  {
    [HttpPost]
    public ActionResult Post(PaymentIntentCaptureRequest request)
    {
      var service = new PaymentIntentService();
      var {span response capture-intent-endpoint}intent{/span} = service.Capture(request.Id, null);

      return Json(intent);
    }

    public class PaymentIntentCaptureRequest
    {
      [JsonProperty("id")]
      public string Id { get; set; }
    }
  }
  {/capture-intent-endpoint}
}`;
